import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { io } from 'socket.io-client';
import api from '../services/api';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  BarChart, Bar, PieChart, Pie, Cell 
} from 'recharts';

import OSForm from './components/OSForm';
import OSList from './components/OSList';
import OSReports from './components/OSReports';
import InsightsDashboard from './components/InsightsDashboard';
import RoboPosVenda from './components/RoboPosVenda';
import TemplatesManager from './components/TemplatesManager';
import WhatsAppPro from './components/WhatsAppPro';
import RoboConfig from './components/RoboConfig';
import FluxoDeCaixa from './RelaFinaStart';
import DREOtica from './Dre';
import Estoque from './Estoque';
import BackupFotos from './components/BackupFotos';
import SimuladorUltraRealista from './components/SimuladorUltraRealista'; 

// --- Utilitários ---
const safeParseUser = () => {
  try { return JSON.parse(localStorage.getItem('vizumUser')) || {}; } 
  catch { return {}; }
};

const formatDateLocal = (dateString) => {
  if (!dateString) return '--/--';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year.slice(2)}`;
  } catch { return dateString; }
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const getFirstName = (fullName = '') => fullName.trim().split(' ')[0] || 'Usuário';

const getInitials = (name = '') => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (!parts.length) return 'US';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const getStatusTone = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('entreg')) return 'success';
  if (s.includes('pronto')) return 'info';
  if (s.includes('laboratório') || s.includes('laboratorio') || s.includes('pedido')) return 'warning';
  return 'neutral';
};

// --- Normalização de Loja / Empresa / Vendedor ---
const normalizarTextoBusca = (valor = '') =>
  String(valor)
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const formatarNomeCapitalizado = (valor = '') =>
  String(valor)
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const descobrirEmpresaOficial = (osLojaString, empresasCadastradas = []) => {
  if (!osLojaString) return null;

  const busca = normalizarTextoBusca(osLojaString);

  // 1. Tenta bater exatamente com o codigoLoja. Ex: V01, V02.
  let empresaEncontrada = empresasCadastradas.find(emp =>
    emp?.codigoLoja && normalizarTextoBusca(emp.codigoLoja) === busca
  );
  if (empresaEncontrada) return empresaEncontrada;

  // 2. Tenta bater exatamente com a unidade.
  empresaEncontrada = empresasCadastradas.find(emp =>
    emp?.unidade && normalizarTextoBusca(emp.unidade) === busca
  );
  if (empresaEncontrada) return empresaEncontrada;

  // 3. Retrocompatibilidade Vizum 01.
  if (
    busca.includes('V01') ||
    busca.includes('VIZUM 01') ||
    busca.includes('VIZUM01') ||
    busca.includes('LOJA 01') ||
    busca.includes('LOJA01') ||
    busca.includes('CORONEL')
  ) {
    return empresasCadastradas.find(emp => {
      const codigo = normalizarTextoBusca(emp?.codigoLoja || '');
      const unidade = normalizarTextoBusca(emp?.unidade || '');
      const nome = normalizarTextoBusca(emp?.nome || emp?.razaoSocial || '');

      return (
        codigo.includes('V01') ||
        unidade.includes('V01') ||
        unidade.includes('VIZUM 01') ||
        unidade.includes('VIZUM01') ||
        unidade.includes('LOJA 01') ||
        unidade.includes('LOJA01') ||
        unidade.includes('CORONEL') ||
        nome.includes('VIZUM 01') ||
        nome.includes('VIZUM01')
      );
    }) || null;
  }

  // 4. Retrocompatibilidade Vizum 02.
  if (
    busca.includes('V02') ||
    busca.includes('VIZUM 02') ||
    busca.includes('VIZUM02') ||
    busca.includes('LOJA 02') ||
    busca.includes('LOJA02') ||
    busca.includes('SEBASTIAO')
  ) {
    return empresasCadastradas.find(emp => {
      const codigo = normalizarTextoBusca(emp?.codigoLoja || '');
      const unidade = normalizarTextoBusca(emp?.unidade || '');
      const nome = normalizarTextoBusca(emp?.nome || emp?.razaoSocial || '');

      return (
        codigo.includes('V02') ||
        unidade.includes('V02') ||
        unidade.includes('VIZUM 02') ||
        unidade.includes('VIZUM02') ||
        unidade.includes('LOJA 02') ||
        unidade.includes('LOJA02') ||
        unidade.includes('SEBASTIAO') ||
        nome.includes('VIZUM 02') ||
        nome.includes('VIZUM02')
      );
    }) || null;
  }

  return null;
};

const normalizarLojaOS = (os, empresasCadastradas = []) => {
  const lojaBruta = os?.loja || os?.unidade || os?.empresa || os?.codigoLoja || 'Principal';
  const empresa = descobrirEmpresaOficial(lojaBruta, empresasCadastradas);

  if (empresa) {
    return empresa.unidade || empresa.codigoLoja || empresa.nome || lojaBruta;
  }

  const busca = normalizarTextoBusca(lojaBruta);

  if (
    busca.includes('V01') ||
    busca.includes('VIZUM 01') ||
    busca.includes('VIZUM01') ||
    busca.includes('LOJA 01') ||
    busca.includes('LOJA01') ||
    busca.includes('CORONEL')
  ) {
    return 'Vizum 01';
  }

  if (
    busca.includes('V02') ||
    busca.includes('VIZUM 02') ||
    busca.includes('VIZUM02') ||
    busca.includes('LOJA 02') ||
    busca.includes('LOJA02') ||
    busca.includes('SEBASTIAO')
  ) {
    return 'Vizum 02';
  }

  return formatarNomeCapitalizado(lojaBruta);
};

/**
 * Agrupa os vendedores duplicados.
 */
const normalizarNomeVendedor = (nomeBruto) => {
  if (!nomeBruto) return 'Não Informado';

  const n = normalizarTextoBusca(nomeBruto);

  if (n.includes('VITOR AFONSO') || n === 'VITOR') return 'Vitor Afonso';
  if (n.includes('ANA BEATRIZ') || n.includes('ANNA BEATRIZ')) return 'Anna Beatriz';
  if (n.includes('ROSILENE')) return 'Rosilene';

  return formatarNomeCapitalizado(nomeBruto);
};

// Cores para os Gráficos - Google Style
const CHART_COLORS = {
  primary: '#1A73E8', 
  success: '#1E8E3E', 
  warning: '#F9AB00', 
  info: '#4285F4',
  danger: '#D93025',  
  neutral: '#80868B'
};

// Logo monocromática em SVG (óculos) para manter visual limpo e profissional.

// --- Ícones Refinados (SaaS Style) strokeWidth 1.5 ---
const Icons = {
  Menu: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>,
  ChevronLeft: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>,
  ChevronRight: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>,
  ChevronDown: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>,
  Logo: () => (
    <span className="brand-logo-mark" aria-hidden="true">
      <svg className="brand-glasses-logo" width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.8 17.2C7.35 14.55 8.4 12.15 9.7 9.8" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/>
        <path d="M25.2 17.2C24.65 14.55 23.6 12.15 22.3 9.8" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/>
        <path d="M12.9 18.5C13.8 17.85 14.8 17.55 16 17.55C17.2 17.55 18.2 17.85 19.1 18.5" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/>
        <path d="M13.5 20.2C13.5 22.65 11.55 24.55 9.05 24.55C6.55 24.55 4.55 22.65 4.55 20.2C4.55 17.75 6.55 15.85 9.05 15.85C11.55 15.85 13.5 17.75 13.5 20.2Z" stroke="currentColor" strokeWidth="2.1"/>
        <path d="M27.45 20.2C27.45 22.65 25.45 24.55 22.95 24.55C20.45 24.55 18.5 22.65 18.5 20.2C18.5 17.75 20.45 15.85 22.95 15.85C25.45 15.85 27.45 17.75 27.45 20.2Z" stroke="currentColor" strokeWidth="2.1"/>
        <path d="M11.25 11.3C12.55 10.15 14.15 9.55 16 9.55C17.85 9.55 19.45 10.15 20.75 11.3" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/>
      </svg>
    </span>
  ),
  Dashboard: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="14" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect></svg>,
  Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
  List: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>,
  Chart: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>,
  TrendingUp: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13 16 9 12 2 19"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>,
  Close: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Robot: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8.01" y2="16"></line><line x1="16" y1="16" x2="16.01" y2="16"></line></svg>,
  AlertCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
  Edit: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
  Message: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
  Box: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
  Search: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
  Mic: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="22"></line></svg>,
  Camera: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>,
  Wallet: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></svg>,
  PieChart: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg>,
  Bolt: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>,
  Power: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line></svg>,
  Award: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>,
  Bronze: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CD7F32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  Silver: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9AA0A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  Gold: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F9AB00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  Diamond: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16 14 19 22 12 17 5 22 8 14 2 9 9 9 12 2"/></svg>,
  MapPin: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
  DownloadCloud: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><polyline points="8 17 12 21 16 17"></polyline><line x1="12" y1="12" x2="12" y2="21"></line><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"></path></svg>,
  Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
  Building: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>,
  Filter: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
};

// --- Configurações ---
const WHATSAPP_STATUS = { CHECKING: 'checking', LOADING: 'loading', CONNECTED: 'connected', DISCONNECTED: 'disconnected' };

const NAV_SECTIONS = [
  { key: 'principal', label: 'Operação principal', defaultOpen: true, helper: 'O que mais importa no balcão', items: [
    { key: 'home', label: 'Central da ótica', icon: <Icons.Dashboard />, destaque: true },
    { key: 'nova', label: 'Nova venda', icon: <Icons.Plus />, destaque: true },
    { key: 'lista', label: 'Consultar O.S.', icon: <Icons.List />, destaque: true },
    { key: 'relatorios', label: 'Relatórios da O.S.', icon: <Icons.Chart />, destaque: true },
  ]},
  { key: 'pos-venda', label: 'Pós-venda premium', defaultOpen: false, helper: 'CRM, WhatsApp e relacionamento', special: true, items: [
    { key: 'robo', label: 'Conversão CRM', icon: <Icons.Robot />, destaque: true },
    { key: 'whatsapp', label: 'WhatsApp conectado', icon: <Icons.Message />, destaque: true },
    { key: 'templates', label: 'Modelos inteligentes', icon: <Icons.Edit /> },
    { key: 'config-vendas', label: 'Configurar régua', icon: <Icons.Bolt /> },
  ]},
  { key: 'gestao', label: 'Gestão da loja', defaultOpen: false, helper: 'Financeiro, estoque e indicadores', items: [
    { key: 'caixa', label: 'Fluxo de caixa', icon: <Icons.Wallet /> },
    { key: 'dre', label: 'DRE da ótica', icon: <Icons.PieChart /> },
    { key: 'estoque', label: 'Estoque de lentes', icon: <Icons.Box /> },
    { key: 'insights', label: 'Insights e metas', icon: <Icons.TrendingUp /> },
  ]},
  { key: 'avancado', label: 'Ferramentas avançadas', defaultOpen: false, helper: 'Recursos usados com menor frequência', items: [
    { key: 'anexar', label: 'Anexos e fotos', icon: <Icons.Camera /> },
    { key: 'simulador', label: 'Simulador óptico', icon: <Icons.Eye /> },
    { key: 'backup', label: 'Backup de fotos', icon: <Icons.DownloadCloud /> },
  ]},
];

const NIVEL_METAS = [
  { nome: 'Iniciante', icone: Icons.Bronze, cor: '#80868B', limite: 0, proximo: 25000 },
  { nome: 'Bronze', icone: Icons.Bronze, cor: '#CD7F32', limite: 25000, proximo: 35000 },
  { nome: 'Prata', icone: Icons.Silver, cor: '#9AA0A6', limite: 35000, proximo: 45000 },
  { nome: 'Ouro', icone: Icons.Gold, cor: '#F9AB00', limite: 45000, proximo: 55000 },
  { nome: 'Diamante', icone: Icons.Diamond, cor: '#4285F4', limite: 55000, proximo: 60000 },
  { nome: 'Platina', icone: Icons.Diamond, cor: '#5F6368', limite: 60000, proximo: Infinity },
];

const getNivelAtual = (totalVendido = 0) => {
  for (let i = NIVEL_METAS.length - 1; i >= 0; i--) {
    if (totalVendido >= NIVEL_METAS[i].limite) return NIVEL_METAS[i];
  }
  return NIVEL_METAS[0];
};

const getProgressoParaProximo = (totalVendido = 0, nivel) => {
  if (nivel.proximo === Infinity) return 100;
  const progresso = ((totalVendido - nivel.limite) / (nivel.proximo - nivel.limite)) * 100;
  return Math.min(100, Math.max(0, progresso));
};

// --- Componentes Extras ---
const CustomTooltip = ({ active, payload, label, isCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-chart-tooltip">
        <p className="tooltip-label">{label}</p>
        <p className="tooltip-value" style={{ color: payload[0].color }}>
          {isCurrency ? formatCurrency(payload[0].value) : payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

const AccordionPanel = ({ title, subtitle, icon, badge, isOpen, toggle, children }) => (
  <section className={`panel-card ${isOpen ? 'open' : ''}`}>
    <div className="panel-header" onClick={toggle} role="button">
      <div className="panel-titles">
        <div className="title-row">
          {icon && <span className="title-icon">{icon}</span>}
          <h3>{title}</h3>
          {badge && <span className="title-badge">{badge}</span>}
        </div>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <div className={`chevron-icon ${isOpen ? 'rotate' : ''}`}><Icons.ChevronDown /></div>
    </div>
    {isOpen && <div className="panel-body">{children}</div>}
  </section>
);

// --- Componente Principal ---
export default function OrdensServico() {
  const [isGlobalLoading, setIsGlobalLoading] = useState(true);
  const [modo, setModo] = useState('home');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [targetOsId, setTargetOsId] = useState(null);
  const [todasOS, setTodasOS] = useState([]);
  const [diasFiltro] = useState(3);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  
  const [whatsappStatus, setWhatsappStatus] = useState(WHATSAPP_STATUS.CHECKING);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [monthlySales, setMonthlySales] = useState(null);
  
  const [lojaAtiva, setLojaAtiva] = useState('Todas');

  const [panelsOpen, setPanelsOpen] = useState({ prioridade: true });
  const [navSectionsOpen, setNavSectionsOpen] = useState(() =>
    NAV_SECTIONS.reduce((acc, section) => ({ ...acc, [section.key]: section.defaultOpen }), {})
  );

  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const profileMenuRef = useRef(null);

  const user = useMemo(() => safeParseUser(), []);
  const empresasCadastradas = useMemo(() => {
    return user?.empresas || user?.empresasCadastradas || user?.lojas || [];
  }, [user]);
  const userName = getFirstName(user.nomeCompleto || '');
  const userInitials = getInitials(user.nomeCompleto || '');
  const socketUrl = api.defaults.baseURL || window.location.origin;

  const togglePanel = (panelKey) => setPanelsOpen(prev => ({ ...prev, [panelKey]: !prev[panelKey] }));
  const toggleNavSection = (sectionKey) => {
    if (isSidebarCollapsed) return;
    setNavSectionsOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const lojasDisponiveis = useMemo(() => {
    const lojasSet = new Set();

    todasOS.forEach(os => {
      lojasSet.add(normalizarLojaOS(os, empresasCadastradas));
    });

    return ['Todas', ...Array.from(lojasSet).sort()];
  }, [todasOS, empresasCadastradas]);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    const cleanTerm = term.replace(/\D/g, ''); 

    return todasOS.filter((os) => {
        const nomeCliente = os.cliente?.nome?.toLowerCase() || '';
        const numOs = os.numeroOS ? String(os.numeroOS) : '';
        const whatsapp = os.whatsapp || os.cliente?.telefone || '';
        const cv = os.produto?.numeroCV?.toLowerCase() || '';

        return (
          nomeCliente.includes(term) || numOs.includes(term) || cv.includes(term) ||
          whatsapp.includes(term) || (cleanTerm && whatsapp.replace(/\D/g, '').includes(cleanTerm))
        );
      }).slice(0, 5); 
  }, [searchTerm, todasOS]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (navigator.vibrate) navigator.vibrate(35);
    setTimeout(() => setToast(null), 3500);
  }, []);

  const closeMenus = useCallback(() => {
    setSidebarOpen(false);
    setIsSearchFocused(false);
    setShowProfileMenu(false);
  }, []);

  const navigate = useCallback((newMode) => {
    if (newMode !== 'relatorios') setTargetOsId(null);
    setModo(newMode);
    closeMenus();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [closeMenus]);

  const handleOpenSpecificOS = useCallback((osId) => {
    setTargetOsId(osId);
    setSearchTerm('');
    setIsSearchFocused(false);
    setModo('relatorios');
  }, []);

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      navigate('lista');
      setIsSearchFocused(false);
    }
  };

  const startVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'pt-BR';
      recognition.onstart = () => { setIsListening(true); showToast('success', 'Ouvindo...'); };
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        setSearchTerm(transcript);
        setIsSearchFocused(true);
        searchInputRef.current?.focus();
      };
      recognition.start();
    } else {
      showToast('error', 'Pesquisa por voz não suportada.');
    }
  };

  const fetchDashDados = useCallback(async () => {
    setIsGlobalLoading(true);
    try {
      const response = await api.get('/ordens-servico');
      setTodasOS(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showToast('error', 'Falha ao carregar dados.');
    } finally {
      setTimeout(() => setIsGlobalLoading(false), 500);
    }
  }, [showToast]);

  const fetchMonthlySales = useCallback(async () => {
    try {
      if (!user?.nomeCompleto) return;
      const response = await api.get(`/api/vendedores/vendas-mes/${encodeURIComponent(user.nomeCompleto)}`);
      if (response.data?.success) {
        setMonthlySales(response.data.totalVendido || 0);
      }
    } catch (error) {}
  }, [user]);

  // --- LÓGICA INTELIGENTE DO WHATSAPP / SOCKET ---
  const checkWhatsappStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/whatsapp/status');
      if (data?.connected) {
        setWhatsappStatus(WHATSAPP_STATUS.CONNECTED);
        setQrCode(null);
        setShowQrModal(false);
        return true;
      }
      setWhatsappStatus((prev) => prev === WHATSAPP_STATUS.LOADING ? prev : WHATSAPP_STATUS.DISCONNECTED);
      return false;
    } catch {
      setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED);
      return false;
    }
  }, []);

  const connectWhatsapp = async () => {
    if (whatsappStatus === WHATSAPP_STATUS.CONNECTED) return;
    
    setShowQrModal(true); // Mostra o modal na hora (com loader)
    setQrCode(null);
    setWhatsappStatus(WHATSAPP_STATUS.LOADING);
    setShowProfileMenu(false);

    try {
      const response = await api.post('/api/whatsapp/connect');
      if (response.data?.message === 'Já conectado.') {
        setWhatsappStatus(WHATSAPP_STATUS.CONNECTED);
        setShowQrModal(false);
        showToast('success', 'Sessão ativa restaurada.');
      }
    } catch (error) {
      setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED);
      setShowQrModal(false);
      showToast('error', 'Falha ao iniciar a conexão.');
    }
  };

  const disconnectWhatsapp = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
    try {
      await api.post('/api/whatsapp/disconnect');
      setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED);
      showToast('success', 'WhatsApp desconectado.');
    } catch {
      showToast('error', 'Erro ao desconectar.');
    }
  };

  useEffect(() => { fetchDashDados(); }, [fetchDashDados]);

  useEffect(() => {
    checkWhatsappStatus();
    const socket = io(socketUrl, { transports: ['websocket', 'polling'] });
    let successTimer; // Timer para o "debounce" do status Connected

    const handleQrReceived = (qrString) => {
      clearTimeout(successTimer); // Cancela qualquer evento falso de CONNECTED
      setQrCode(qrString);
      setWhatsappStatus(WHATSAPP_STATUS.LOADING);
      setShowQrModal(true); // Garante que o modal fique aberto
    };

    const handleConnectedReceived = () => {
      clearTimeout(successTimer);
      // Espera 1.5s para garantir que não vai piscar para QRCODE
      successTimer = setTimeout(() => {
        setWhatsappStatus(WHATSAPP_STATUS.CONNECTED);
        setQrCode(null);
        setShowQrModal(false);
        showToast('success', 'WhatsApp conectado com sucesso!');
      }, 1500); 
    };

    const handleDisconnectedReceived = () => {
      clearTimeout(successTimer);
      setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED);
      setQrCode(null);
    };

    // Escuta os eventos estruturados (que aparecem no log)
    socket.on('whatsapp_status', (data) => {
      if (data?.qrCode) handleQrReceived(data.qrCode);
      else if (data?.status === 'QRCODE' && data?.qr) handleQrReceived(data.qr);
      else if (data?.status === 'CONNECTED' && data?.connected) handleConnectedReceived();
      else if (data?.status === 'DISCONNECTED') handleDisconnectedReceived();
    });

    // Escuta os eventos genéricos (backup)
    socket.on('status', (payload) => {
      const s = typeof payload === 'object' ? payload?.status : payload;
      if (s === 'QRCODE' && payload?.qrCode) handleQrReceived(payload.qrCode);
      if (['qrReadSuccess', 'isLogged'].includes(s)) handleConnectedReceived();
      if (['DISCONNECTED', 'browserClose'].includes(s)) handleDisconnectedReceived();
    });

    // Antigo
    socket.on('qrCode', (qr) => handleQrReceived(typeof qr === 'object' ? qr.code : qr));

    return () => {
      clearTimeout(successTimer);
      socket.disconnect();
    };
  }, [socketUrl, checkWhatsappStatus, showToast]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') { 
        closeMenus(); 
        if (showQrModal) setShowQrModal(false);
      }
    };
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) setIsSearchFocused(false);
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => { window.removeEventListener('keydown', handleKeyDown); document.removeEventListener('mousedown', handleClickOutside); };
  }, [showQrModal, closeMenus]);

  const dashboardKpis = useMemo(() => {
    const hojeStr = new Date().toISOString().split('T')[0];
    const mesAtualStr = hojeStr.substring(0, 7); 
    const limiteAlerta = new Date(); limiteAlerta.setDate(limiteAlerta.getDate() + diasFiltro);

    let kpiLab = 0, totalEntregues = 0, totalProntoLoja = 0, valorHoje = 0, valorMes = 0, qtdeVendasMes = 0;
    const proximas = [], vendedoresMap = {};
    const diasNoMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const vendasDiariasMap = Array.from({ length: diasNoMes }, (_, i) => ({ dia: `D${i + 1}`, valor: 0 }));

    todasOS
      .filter(os => {
        const lojaNormalizada = normalizarLojaOS(os, empresasCadastradas);
        return lojaAtiva === 'Todas' || lojaNormalizada === lojaAtiva;
      })
      .forEach((os) => {
      const valor = parseFloat(os.valorTotal || os.pagamento?.valorTotal || os.sinal || 0);
      if (['No laboratório', 'Pedido realizado'].includes(os.status)) kpiLab++;
      if (os.status === 'Entregue') totalEntregues++;
      if (os.status === 'Pronto na loja') totalProntoLoja++;

      if (os.dataCompra) {
        if (os.dataCompra.startsWith(hojeStr)) valorHoje += valor;
        if (os.dataCompra.startsWith(mesAtualStr)) {
          qtdeVendasMes++; valorMes += valor;
          const vendedor = normalizarNomeVendedor(os.vendedor || os.usuario || os.vendedorNome || os.createdBy?.nomeCompleto || 'Não Informado');
          vendedoresMap[vendedor] = (vendedoresMap[vendedor] || 0) + valor;
          const diaVenda = parseInt(os.dataCompra.split('-')[2], 10);
          if (diaVenda && vendasDiariasMap[diaVenda - 1]) vendasDiariasMap[diaVenda - 1].valor += valor;
        }
      }

      if (os.dataEntrega && !['Entregue', 'Pronto na loja'].includes(os.status)) {
        const d = new Date(os.dataEntrega), ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
        if (d <= limiteAlerta && d >= ontem) proximas.push(os);
      }
    });

    return {
      kpiLab, totalEntregues, totalProntoLoja, valorHoje, valorMes, 
      ticketMedio: qtdeVendasMes > 0 ? valorMes / qtdeVendasMes : 0, 
      rankingVendedores: Object.entries(vendedoresMap).map(([name, valor]) => ({ name, valor })).sort((a, b) => b.valor - a.valor).slice(0, 5), 
      dadosStatus: [
        { name: 'Laboratório', value: kpiLab, color: CHART_COLORS.warning },
        { name: 'Na Loja', value: totalProntoLoja, color: CHART_COLORS.info },
        { name: 'Entregues', value: totalEntregues, color: CHART_COLORS.success },
      ].filter(s => s.value > 0), 
      vendasDiariasMap,
      entregasProximas: proximas.sort((a, b) => new Date(a.dataEntrega) - new Date(b.dataEntrega)),
    };
  }, [todasOS, diasFiltro, lojaAtiva, empresasCadastradas]);

  const saudacao = new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite';
  
  const nivelAtual = getNivelAtual(monthlySales || 0);
  const progressoMeta = getProgressoParaProximo(monthlySales || 0, nivelAtual);
  const NivelIcon = nivelAtual.icone || Icons.Award;

  return (
    <div className={`vizum-app-shell ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <GlobalStyles />

      {isGlobalLoading && (
        <div className="global-loader-overlay">
          <div className="loader-spinner"></div>
        </div>
      )}

      <div className={`mobile-sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* --- Navegação Lateral --- */}
      <aside className={`app-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="brand-header">
           <button className="icon-btn mobile-only" onClick={() => setSidebarOpen(false)}><Icons.Menu /></button>
           <div className="brand-logo"><Icons.Logo /> <span className="brand-text">Lumix Óticas</span></div>
        </div>

        <nav className="nav-menu custom-scrollbar">
          {NAV_SECTIONS.map((section) => {
            const isOpen = navSectionsOpen[section.key];
            const shouldShowItems = isSidebarCollapsed || isOpen;

            return (
              <div className={`nav-section nav-section-${section.key} ${section.special ? 'nav-section-special' : ''} ${isOpen ? 'open' : ''}`} key={section.key}>
                {!isSidebarCollapsed && (
                  <button className="nav-section-header" onClick={() => toggleNavSection(section.key)} type="button">
                    <span className="nav-section-title-wrap">
                      <span className="nav-section-title">{section.label}</span>
                      {section.helper && <span className="nav-section-helper">{section.helper}</span>}
                    </span>
                    <span className={`nav-section-chevron ${isOpen ? 'rotate' : ''}`}><Icons.ChevronDown /></span>
                  </button>
                )}

                {shouldShowItems && (
                  <div className="nav-section-items">
                    {section.items.map((item) => (
                      <NavItem
                        key={item.key}
                        icon={item.icon}
                        label={item.label}
                        active={modo === item.key}
                        collapsed={isSidebarCollapsed}
                        destaque={item.destaque}
                        onClick={() => navigate(item.key)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <main className="app-main">
        {/* --- Barra Superior --- */}
        <header className="app-topbar">
          <div className="topbar-left">
            <button className="icon-btn desktop-only" onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}><Icons.Menu /></button>
            <button className="icon-btn mobile-only" onClick={() => setSidebarOpen(true)}><Icons.Menu /></button>
          </div>

          <div className="topbar-center" ref={searchContainerRef}>
            <div className={`search-omnibox ${isSearchFocused ? 'focused' : ''}`}>
              <Icons.Search />
              <input
                ref={searchInputRef} type="text" placeholder="Pesquisar OS, cliente, CV..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onKeyDown={handleSearchSubmit}
              />
              <button className={`icon-btn-mic ${isListening ? 'listening' : ''}`} onClick={startVoiceSearch} title="Pesquisar por voz">
                <Icons.Mic />
              </button>
            </div>

            {isSearchFocused && searchTerm.trim() && (
              <div className="search-dropdown">
                {searchResults.length > 0 ? (
                  <ul className="search-results-list">
                    {searchResults.map((os) => (
                      <li key={os._id} className="search-result-item" onClick={() => handleOpenSpecificOS(os._id)}>
                        <div className="result-icon"><Icons.Box /></div>
                        <div className="result-info">
                          <span className="result-name">{os.cliente?.nome || 'Sem nome'}</span>
                          <span className="result-sub">OS #{os.numeroOS || '---'} • 📞 {os.whatsapp || os.cliente?.telefone || '---'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="search-empty">Nenhum resultado encontrado.</div>
                )}
              </div>
            )}
          </div>

          <div className="topbar-right">
            <div className="user-profile-wrapper" ref={profileMenuRef}>
              <button className="avatar-btn" onClick={() => { setShowProfileMenu(!showProfileMenu); if(monthlySales===null) fetchMonthlySales(); }}>
                {userInitials}
                <span className={`status-dot ${whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'online' : 'offline'}`}></span>
              </button>

              {showProfileMenu && (
                <div className="profile-menu">
                  {/* Seção 1: Conta */}
                  <div className="profile-account-section">
                    <div className="profile-large-avatar">{userInitials}</div>
                    <div className="profile-account-info">
                      <strong>{user.nomeCompleto || 'Usuário'}</strong>
                      <span>{user.email || 'usuario@lumix.com'}</span>
                    </div>
                  </div>

                  {/* Seção 2: Vendas e Nível */}
                  <div className="profile-level-section">
                    <div className="level-header">
                       <div className="level-badge" style={{ color: nivelAtual.cor, backgroundColor: `${nivelAtual.cor}15` }}>
                          <NivelIcon /> <span>{nivelAtual.nome}</span>
                       </div>
                       <span className="level-sales">{formatCurrency(monthlySales || 0)}</span>
                    </div>
                    {nivelAtual.proximo !== Infinity && (
                       <div className="level-progress-container">
                          <div className="progress-labels">
                             <span>Progresso da Meta</span>
                             <span>Falta {formatCurrency(nivelAtual.proximo - (monthlySales || 0))}</span>
                          </div>
                          <div className="progress-track">
                             <div className="progress-fill" style={{ width: `${progressoMeta}%`, backgroundColor: nivelAtual.cor }}></div>
                          </div>
                       </div>
                    )}
                  </div>

                  {/* Seção 3: Automação WhatsApp */}
                  <div className="profile-wa-section">
                    <div className={`wa-status-card ${whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'connected' : ''}`}>
                       <div className="wa-icon-box"><Icons.Message /></div>
                       <div className="wa-info-box">
                         <strong>Robô WhatsApp</strong>
                         <p>{whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'Sessão Ativa' : 'Desconectado'}</p>
                       </div>
                    </div>
                    <button 
                       className={`wa-action-btn ${whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'danger' : 'primary'}`}
                       onClick={whatsappStatus === WHATSAPP_STATUS.CONNECTED ? disconnectWhatsapp : connectWhatsapp}
                     >
                       {whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'Desconectar Dispositivo' : 'Conectar agora'}
                     </button>
                  </div>

                  {/* Seção 4: Sair */}
                  <div className="profile-footer">
                     <button className="btn-logout-google" onClick={() => window.location.href = '/home'}>
                        <Icons.Power /> Sair da conta
                     </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* --- Conteúdo Principal --- */}
        <section className="content-scroll custom-scrollbar">
          {modo === 'home' && (
            <div className="dashboard-layout clean-dashboard">
              <section className="clean-header-card">
                <div className="clean-header-info">
                  <span className="clean-eyebrow">Painel da ótica</span>
                  <h1>{saudacao}, {userName}</h1>
                  <p>Operação simples: vender, consultar, acompanhar e cuidar do pós-venda.</p>
                </div>

                <div className="clean-header-actions">
                  <div className="clean-store-select">
                    <span>Loja</span>
                    <div className="store-filter-chip clean-store-chip">
                      <Icons.Filter />
                      <select value={lojaAtiva} onChange={(e) => setLojaAtiva(e.target.value)}>
                        {lojasDisponiveis.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <Icons.ChevronDown />
                    </div>
                  </div>

                  <div className="clean-action-row">
                    <button className="clean-primary-btn" onClick={() => navigate('nova')}>
                      <Icons.Plus /> Nova venda
                    </button>
                    <button className="clean-secondary-btn" onClick={() => navigate('lista')}>
                      <Icons.Search /> Consultar
                    </button>
                  </div>
                </div>
              </section>

              <section className="clean-kpi-grid">
                <CleanMetric title="Hoje" value={formatCurrency(dashboardKpis.valorHoje)} subtitle="Faturamento" />
                <CleanMetric title="Mês" value={formatCurrency(dashboardKpis.valorMes)} subtitle="Faturamento" />
                <CleanMetric title="Ticket" value={formatCurrency(dashboardKpis.ticketMedio)} subtitle="Média por venda" />
                <CleanMetric title="Laboratório" value={dashboardKpis.kpiLab} subtitle="Em andamento" />
              </section>

              <section className="clean-main-grid">
                <div className="clean-card clean-chart-card">
                  <div className="clean-card-header">
                    <div><span>Vendas</span><h3>Fluxo do mês</h3></div>
                    <button className="clean-link-btn" onClick={() => navigate('relatorios')}>Relatórios</button>
                  </div>
                  <div className="chart-wrapper clean-chart-wrapper">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={dashboardKpis.vendasDiariasMap} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
                        <defs><linearGradient id="cleanBlue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.16}/><stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                        <XAxis dataKey="dia" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#5F6368' }} minTickGap={20} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#5F6368' }} tickFormatter={(val) => `R$${(val/1000).toFixed(0)}k`} />
                        <RechartsTooltip content={<CustomTooltip isCurrency />} cursor={{ stroke: CHART_COLORS.primary, strokeWidth: 1, strokeDasharray: '3 3' }} />
                        <Area type="monotone" dataKey="valor" stroke={CHART_COLORS.primary} strokeWidth={2.2} fillOpacity={1} fill="url(#cleanBlue)" activeDot={{ r: 4 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="clean-card clean-status-card">
                  <div className="clean-card-header"><div><span>Operação</span><h3>Status</h3></div></div>
                  <div className="clean-status-body">
                    <ResponsiveContainer width="100%" height={128}>
                      <PieChart>
                        <Pie data={dashboardKpis.dadosStatus} innerRadius={38} outerRadius={56} paddingAngle={2} dataKey="value" stroke="none">
                          {dashboardKpis.dadosStatus.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="clean-status-list">
                      {dashboardKpis.dadosStatus.map(item => (
                        <div className="clean-status-item" key={item.name}>
                          <span className="dot" style={{ background: item.color }}></span><span>{item.name}</span><strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section className="clean-bottom-grid">
                <div className="clean-card">
                  <div className="clean-card-header">
                    <div><span>Prioridade</span><h3>Atenção da loja</h3></div>
                    {dashboardKpis.entregasProximas.length > 0 && <strong className="clean-count-badge">{dashboardKpis.entregasProximas.length}</strong>}
                  </div>
                  <div className="clean-priority-list custom-scrollbar">
                    {dashboardKpis.entregasProximas.length > 0 ? (
                      dashboardKpis.entregasProximas.slice(0, 6).map(os => (
                        <button className="clean-priority-item" key={os._id} onClick={() => handleOpenSpecificOS(os._id)}>
                          <span className="clean-priority-main"><strong>{os.cliente?.nome || `O.S. #${os.numeroOS}`}</strong><small>OS #{os.numeroOS || '---'} • {normalizarLojaOS(os, empresasCadastradas)}</small></span>
                          <span className={`clean-tag ${getStatusTone(os.status)}`}>{formatDateLocal(os.dataEntrega)}</span>
                        </button>
                      ))
                    ) : (
                      <div className="clean-empty-state"><strong>Tudo em dia</strong><p>Nenhuma entrega crítica no momento.</p></div>
                    )}
                  </div>
                </div>

                <div className="clean-card">
                  <div className="clean-card-header"><div><span>Equipe</span><h3>Top vendedores</h3></div><button className="clean-link-btn" onClick={() => navigate('insights')}>Insights</button></div>
                  <div className="chart-wrapper clean-seller-chart">
                    <ResponsiveContainer width="100%" height={190}>
                      <BarChart data={dashboardKpis.rankingVendedores} margin={{ top: 10, right: 8, left: -18, bottom: 0 }} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#3C4043' }} width={78} />
                        <RechartsTooltip content={<CustomTooltip isCurrency />} cursor={{ fill: '#F8FAFD' }} />
                        <Bar dataKey="valor" fill={CHART_COLORS.info} radius={[0, 4, 4, 0]} barSize={10}>{dashboardKpis.rankingVendedores.map((e, i) => <Cell key={i} fill={i === 0 ? CHART_COLORS.primary : '#8AB4F8'} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </section>
            </div>
          )}
          {modo !== 'home' && (
            <div className="module-container">
              {modo === 'nova' && <OSForm onCancel={() => navigate('home')} onSuccess={() => { navigate('lista'); fetchDashDados(); }} />}
              {modo === 'lista' && <OSList onBack={() => navigate('home')} />}
              {modo === 'simulador' && <SimuladorUltraRealista />}
              {modo === 'robo' && <RoboPosVenda />}
              {modo === 'config-vendas' && <RoboConfig />}
              {modo === 'templates' && <TemplatesManager />}
              {modo === 'whatsapp' && <WhatsAppPro />}
              {modo === 'relatorios' && <OSReports onBack={() => navigate('home')} initialOsId={targetOsId} />}
              {modo === 'insights' && <InsightsDashboard onBack={() => navigate('home')} />}
              {modo === 'caixa' && <FluxoDeCaixa onOpenOS={handleOpenSpecificOS} />}
              {modo === 'dre' && <DREOtica />}
              {modo === 'estoque' && <Estoque />}
              {modo === 'backup' && <BackupFotos />}
            </div>
          )}
        </section>
      </main>


      {/* OVERLAY PESQUISA POR VOZ - Estilo Google */}
      {isListening && (
        <div className="voice-search-overlay">
          <div className="voice-search-card">
            <div className="voice-pulse-ring ring-one"></div>
            <div className="voice-pulse-ring ring-two"></div>
            <div className="voice-big-mic"><Icons.Mic /></div>
            <h3>Ouvindo...</h3>
            <p>Fale o nome do cliente, número da OS ou CV</p>
            <div className="voice-wave"><span></span><span></span><span></span><span></span><span></span></div>
          </div>
        </div>
      )}

      {/* MODAL QR CODE WHATSAPP - Animação de Carregamento e QR */}
      {showQrModal && (
        <div className="modal-backdrop">
          <div className="modal-box qr-modal">
            <div className="modal-top">
              <div className="modal-titles">
                <h3>Conectar WhatsApp</h3>
                <p>Abra o app no celular, vá em "Aparelhos conectados" e aponte para a tela.</p>
              </div>
              <button onClick={() => setShowQrModal(false)} className="icon-btn-close"><Icons.Close /></button>
            </div>
            
            <div className="qr-container">
              {qrCode ? (
                 <div className="qr-code-fade-in">
                   {qrCode.startsWith('data:image') ? <img src={qrCode} alt="QR Code" /> : <QRCodeCanvas value={qrCode} size={200} level="L" />}
                 </div>
              ) : (
                 <div className="qr-loader">
                    <div className="loader-spinner-blue"></div>
                    <span>Gerando código seguro...</span>
                 </div>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`system-toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

// --- Componentes Menores ---
const NavItem = ({ icon, label, active, onClick, collapsed, destaque }) => (
  <button className={`nav-item ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''} ${destaque ? 'featured' : ''}`} onClick={onClick} title={collapsed ? label : ''}>
    <div className="nav-icon">{icon}</div>
    {!collapsed && <span className="nav-label">{label}</span>}
  </button>
);

const StatCard = ({ title, value, color }) => (
  <div className={`stat-card border-${color}`}>
    <span className="stat-title">{title}</span>
    <strong className="stat-value">{value}</strong>
  </div>
);

const CleanMetric = ({ title, value, subtitle }) => (
  <div className="clean-metric-card">
    <span>{title}</span>
    <strong>{value}</strong>
    <p>{subtitle}</p>
  </div>
);

// --- CSS SaaS Google Style (DPI Menor, Alta Densidade, M3) ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    :root {
      /* Palette baseada no Google Material 3 / Workspace */
      --bg-color: #F8FAFD;
      --surface: #FFFFFF;
      --surface-hover: #F1F3F4;
      --text-main: #202124;
      --text-sec: #5F6368;
      --border: #DADCE0;
      
      --google-blue: #1A73E8;
      --google-blue-light: #E8F0FE;
      --google-blue-dark: #174EA6;
      
      --google-green: #1E8E3E;
      --google-red: #D93025;
      --google-yellow: #F9AB00;

      /* Low DPI Settings - ALTA DENSIDADE TOTAL */
      --base-font: 11px;
      --nav-width: 220px;
      --nav-collapsed: 60px;
      --topbar-h: 50px;
      
      --radius-sm: 6px;
      --radius-md: 10px;
      --radius-lg: 16px;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; outline: none; font-family: 'Inter', -apple-system, sans-serif; -webkit-font-smoothing: antialiased; }
    html, body, #root { width: 100%; height: 100dvh; overflow: hidden; background: var(--bg-color); color: var(--text-main); font-size: var(--base-font); }
    button, input, select { font: inherit; border: none; background: none; }
    button { cursor: pointer; }

    /* Scrollbar discreta e elegante */
    .custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(95, 99, 104, 0.28) transparent; }
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(95, 99, 104, 0.22); border-radius: 999px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(95, 99, 104, 0.38); }

    .vizum-app-shell { display: flex; width: 100vw; height: 100dvh; overflow: hidden; }

    /* --- SIDEBAR --- */
    .app-sidebar { width: var(--nav-width); background: var(--bg-color); flex-shrink: 0; display: flex; flex-direction: column; transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1); z-index: 40; border-right: none; }
    .sidebar-collapsed .app-sidebar { width: var(--nav-collapsed); }
    
    .brand-header { height: var(--topbar-h); display: flex; align-items: center; padding: 0 12px; gap: 8px; }
    .brand-logo { display: flex; align-items: center; gap: 8px; color: var(--google-blue); font-weight: 700; font-size: 14px; letter-spacing: -0.02em; }
    .brand-logo-mark { width: 32px; height: 32px; border-radius: 13px; display: grid; place-items: center; flex-shrink: 0; color: #202124; background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%); border: 1px solid rgba(218, 220, 224, 0.95); box-shadow: 0 2px 8px rgba(60, 64, 67, 0.10), inset 0 1px 0 rgba(255,255,255,0.85); overflow: hidden; }
    .brand-glasses-logo { width: 23px; height: 23px; display: block; }
    .brand-logo:hover .brand-logo-mark { color: var(--google-blue); border-color: rgba(26, 115, 232, 0.28); box-shadow: 0 3px 10px rgba(26, 115, 232, 0.14); }
    .brand-text { white-space: nowrap; }
    .sidebar-collapsed .brand-text { display: none; }
    
    .nav-menu { flex: 1; overflow-y: auto; padding: 8px 9px 10px; display: flex; flex-direction: column; gap: 7px; }
    .nav-section {
      border-radius: 18px;
      padding: 3px;
      position: relative;
      border: 1px solid transparent;
      transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
    }
    .nav-section-principal.open {
      background:
        linear-gradient(#F8FAFD, #F8FAFD) padding-box,
        linear-gradient(135deg, #4285F4, #34A853, #FBBC04, #EA4335) border-box;
      border-color: transparent;
      box-shadow: 0 8px 22px rgba(60, 64, 67, 0.07);
    }
    .nav-section-special {
      background:
        linear-gradient(135deg, rgba(232, 240, 254, 0.82), rgba(230, 244, 234, 0.72)) padding-box,
        linear-gradient(135deg, rgba(66, 133, 244, 0.65), rgba(52, 168, 83, 0.65), rgba(251, 188, 4, 0.58), rgba(234, 67, 53, 0.58)) border-box;
      border-color: transparent;
      box-shadow: 0 10px 25px rgba(26, 115, 232, 0.08);
    }
    .nav-section-special::before {
      content: '';
      position: absolute;
      left: 13px;
      top: 11px;
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: #34A853;
      box-shadow: 10px 0 0 #FBBC04, 20px 0 0 #EA4335, 30px 0 0 #4285F4;
      opacity: 0.85;
    }
    .nav-section-header {
      width: 100%; min-height: 32px; padding: 5px 10px 5px 12px;
      display: flex; align-items: center; justify-content: space-between;
      border-radius: 15px; color: var(--text-sec); font-size: 10px;
      font-weight: 700; text-transform: uppercase; letter-spacing: 0.045em;
      transition: background 0.15s, color 0.15s;
    }
    .nav-section-special .nav-section-header { padding-left: 50px; color: #174EA6; }
    .nav-section-header:hover { background: rgba(241, 243, 244, 0.78); color: var(--text-main); }
    .nav-section-title-wrap { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.15; gap: 2px; }
    .nav-section-title { white-space: nowrap; }
    .nav-section-helper { font-size: 8.8px; font-weight: 600; letter-spacing: 0; text-transform: none; color: #5F6368; opacity: 0.88; }
    .nav-section-chevron { display: flex; align-items: center; transition: transform 0.18s ease; opacity: 0.7; }
    .nav-section-chevron.rotate { transform: rotate(180deg); }
    .nav-section-items { display: flex; flex-direction: column; gap: 2px; margin-top: 2px; }

    .nav-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 0 12px; height: 34px; border-radius: 17px; color: var(--text-main); font-weight: 500; transition: background 0.15s, transform 0.15s, box-shadow 0.15s; margin-bottom: 1px;}
    .nav-item:hover { background: var(--surface-hover); transform: translateX(1px); }
    .nav-item.featured { height: 38px; font-weight: 700; background: rgba(255, 255, 255, 0.82); box-shadow: inset 0 0 0 1px rgba(218, 220, 224, 0.85); }
    .nav-item.featured:hover { background: #EEF3FD; box-shadow: inset 0 0 0 1px rgba(66, 133, 244, 0.18); }
    .nav-section-special .nav-item.featured {
      color: #174EA6;
      background: #FFFFFF;
      box-shadow: 0 4px 12px rgba(26, 115, 232, 0.10), inset 0 0 0 1px rgba(66, 133, 244, 0.16);
    }
    .nav-item.active { background: var(--google-blue-light); color: var(--google-blue-dark); font-weight: 800; }
    .nav-section-special .nav-item.active { background: #D2E3FC; color: #174EA6; }
    .nav-icon { display: flex; align-items: center; justify-content: center; width: 20px; color: inherit; }
    .sidebar-collapsed .nav-section { display: flex; flex-direction: column; gap: 2px; padding: 0; background: transparent; border: none; box-shadow: none; }
    .sidebar-collapsed .nav-section::before { display: none; }
    .sidebar-collapsed .nav-section-header { display: none; }
    .sidebar-collapsed .nav-item { justify-content: center; padding: 0; height: 38px; border-radius: 19px;}

    /* --- APP MAIN --- */
    .app-main { flex: 1; display: flex; flex-direction: column; min-width: 0; background: var(--surface); border-radius: 16px 0 0 0; box-shadow: -1px 0 0 var(--border); overflow: hidden;}
    .sidebar-collapsed .app-main { border-radius: 0; box-shadow: none; border-left: 1px solid var(--border);}

    /* --- TOPBAR --- */
    .app-topbar { height: var(--topbar-h); display: flex; align-items: center; justify-content: space-between; padding: 0 16px; background: var(--surface); border-bottom: 1px solid transparent; flex-shrink: 0; }
    .topbar-left { display: flex; align-items: center; width: 40px; }
    
    /* OMNIBOX (Pesquisa Branca / Google) */
    .topbar-center { flex: 1; display: flex; justify-content: center; position: relative; max-width: 580px; margin: 0 16px;}
    .search-omnibox { 
      width: 100%; height: 38px; 
      background: #FFFFFF; 
      border-radius: 19px; 
      display: flex; align-items: center; 
      padding: 0 6px 0 14px; gap: 8px; 
      transition: box-shadow 0.2s, background 0.2s; 
      border: 1px solid var(--border);
      box-shadow: 0 1px 2px rgba(0,0,0,0.03);
    }
    .search-omnibox:hover { box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
    .search-omnibox.focused { border-color: transparent; box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 19px 19px 0 0; }
    .search-omnibox input { flex: 1; font-size: 12px; color: var(--text-main); font-weight: 500;}
    .search-omnibox input::placeholder { color: var(--text-sec); font-weight: 400;}
    .search-omnibox > svg { color: var(--text-sec); width: 14px; height: 14px;}
    
    .icon-btn-mic { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; color: var(--google-blue); transition: 0.2s; }
    .icon-btn-mic:hover { background: #f1f3f4; }
    .icon-btn-mic.listening { animation: pulseMic 1.5s infinite; color: var(--google-red); background: #FCE8E6;}

    .search-dropdown { position: absolute; top: 38px; left: 0; width: 100%; background: var(--surface); border: 1px solid transparent; border-radius: 0 0 19px 19px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); z-index: 100; padding: 6px 0;}
    .search-results-list { list-style: none; }
    .search-result-item { display: flex; align-items: center; gap: 10px; padding: 8px 14px; cursor: pointer; border-left: 3px solid transparent;}
    .search-result-item:hover { background: var(--surface-hover); border-left: 3px solid var(--google-blue);}
    .result-icon { color: var(--text-sec); }
    .result-info { display: flex; flex-direction: column; }
    .result-name { font-size: 11.5px; font-weight: 500; }
    .result-sub { font-size: 10px; color: var(--text-sec); }
    .search-empty { padding: 12px; text-align: center; color: var(--text-sec); font-size: 11px; }

    .topbar-right { display: flex; align-items: center; justify-content: flex-end; width: 40px; }
    
    .avatar-btn { width: 30px; height: 30px; border-radius: 50%; background: var(--google-blue); color: white; font-weight: 600; font-size: 11px; display: grid; place-items: center; position: relative; border: 2px solid transparent; transition: 0.2s;}
    .avatar-btn:hover { border-color: var(--google-blue-light); box-shadow: 0 1px 3px rgba(0,0,0,0.2);}
    .status-dot { position: absolute; bottom: -2px; right: -2px; width: 10px; height: 10px; border-radius: 50%; border: 2px solid var(--surface); }
    .status-dot.online { background: var(--google-green); }
    .status-dot.offline { background: var(--google-red); }

    /* MENU DE PERFIL GOOGLE STYLE */
    .profile-menu { position: absolute; top: 44px; right: 0; background: var(--surface); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); width: 280px; z-index: 100; overflow: hidden;}
    
    .profile-account-section { display: flex; align-items: center; gap: 12px; padding: 14px; border-bottom: 1px solid var(--border); background: var(--surface); }
    .profile-large-avatar { width: 42px; height: 42px; border-radius: 50%; background: var(--google-blue); color: white; font-size: 16px; font-weight: 600; display: grid; place-items: center; flex-shrink: 0;}
    .profile-account-info { display: flex; flex-direction: column; min-width: 0;}
    .profile-account-info strong { font-size: 13px; font-weight: 600; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;}
    .profile-account-info span { font-size: 10.5px; color: var(--text-sec); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;}

    .profile-level-section { padding: 10px 14px; border-bottom: 1px solid var(--border); background: #fdfdfd; }
    .level-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .level-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 12px; font-weight: 600; font-size: 10px; }
    .level-sales { font-size: 13px; font-weight: 700; color: var(--text-main); }
    .level-progress-container { display: flex; flex-direction: column; gap: 4px; }
    .progress-labels { display: flex; justify-content: space-between; font-size: 9.5px; color: var(--text-sec); font-weight: 500;}
    .progress-track { width: 100%; height: 4px; background: #E8EAED; border-radius: 2px; overflow: hidden; }
    .progress-fill { height: 100%; border-radius: 2px; transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1); }

    .profile-wa-section { padding: 10px 14px; border-bottom: 1px solid var(--border); display: flex; flex-direction: column; gap: 8px;}
    .wa-status-card { display: flex; align-items: center; gap: 8px; padding: 6px; border-radius: 8px; background: #F8FAFD; border: 1px solid var(--border); transition: 0.2s;}
    .wa-status-card.connected { background: #E6F4EA; border-color: transparent; }
    .wa-icon-box { width: 28px; height: 28px; border-radius: 50%; background: var(--surface); display: grid; place-items: center; color: var(--text-sec); box-shadow: 0 1px 2px rgba(0,0,0,0.05);}
    .wa-status-card.connected .wa-icon-box { color: var(--google-green); }
    .wa-info-box { display: flex; flex-direction: column; }
    .wa-info-box strong { font-size: 11px; font-weight: 600; color: var(--text-main);}
    .wa-info-box p { font-size: 9.5px; color: var(--text-sec); margin-top: 1px;}
    
    .wa-action-btn { width: 100%; padding: 6px; border-radius: 6px; font-size: 11px; font-weight: 600; transition: 0.2s; border: none; cursor: pointer;}
    .wa-action-btn.primary { background: var(--google-blue-light); color: var(--google-blue-dark); }
    .wa-action-btn.primary:hover { background: #d2e3fc; }
    .wa-action-btn.danger { background: #FCE8E6; color: var(--google-red); }
    .wa-action-btn.danger:hover { background: #fad2cf; }

    .profile-footer { padding: 8px 14px; background: var(--surface); }
    .btn-logout-google { width: 100%; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 6px; border-radius: 6px; font-size: 11px; color: var(--text-sec); font-weight: 500; transition: 0.2s; border: 1px solid transparent;}
    .btn-logout-google:hover { background: var(--surface-hover); color: var(--text-main); }


    /* --- CONTENT AREA --- */
    .content-scroll { flex: 1; overflow-y: auto; padding: 16px 20px; background: var(--surface); }
    .dashboard-layout { max-width: 1000px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
    
    /* Cabeçalho Dashboard e Filter Chip Premium */
    .dashboard-header-row { display: flex; justify-content: space-between; align-items: flex-start; }
    .greeting-block h2 { font-size: 20px; font-weight: 400; color: var(--text-main); margin-bottom: 6px;}
    
    .store-filter-chip {
      display: inline-flex; align-items: center; gap: 6px; 
      background: #FFFFFF; border: 1px solid var(--border); 
      padding: 4px 10px; border-radius: 16px; 
      font-size: 11px; font-weight: 500; color: #3C4043; 
      cursor: pointer; transition: all 0.2s ease;
      box-shadow: 0 1px 2px rgba(0,0,0,0.02);
    }
    .store-filter-chip:hover { background: #F1F3F4; border-color: #BDC1C6; box-shadow: 0 1px 4px rgba(0,0,0,0.05);}
    .store-filter-chip svg { color: var(--google-blue); width: 12px; height: 12px;}
    .store-filter-chip select { cursor: pointer; color: inherit; font-weight: 600; appearance: none; padding-right: 2px; }

    /* Botão Nova Venda Principal */
    .btn-nova-venda { 
      display: flex; align-items: center; gap: 6px; 
      background: var(--google-blue); color: white; 
      padding: 0 16px; height: 36px; border-radius: 18px; 
      font-size: 12px; font-weight: 500; 
      transition: 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .btn-nova-venda:hover { background: var(--google-blue-dark); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }

    .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .stat-card { border: 1px solid var(--border); border-radius: var(--radius-md); padding: 14px; display: flex; flex-direction: column; gap: 4px; box-shadow: 0 1px 2px rgba(0,0,0,0.02);}
    .stat-card.border-blue { border-top: 3px solid var(--google-blue); }
    .stat-card.border-green { border-top: 3px solid var(--google-green); }
    .stat-card.border-neutral { border-top: 3px solid var(--text-sec); }
    .stat-title { font-size: 10.5px; color: var(--text-sec); font-weight: 500;}
    .stat-value { font-size: 22px; font-weight: 400; color: var(--text-main); }

    .charts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .full-span { grid-column: 1 / -1; }
    .card { border: 1px solid var(--border); border-radius: var(--radius-md); padding: 14px; box-shadow: 0 1px 2px rgba(0,0,0,0.02);}
    .card-header { font-size: 12.5px; font-weight: 500; margin-bottom: 12px; color: var(--text-main); }
    .chart-wrapper { width: 100%; }
    .pie-wrapper { display: flex; flex-direction: column; align-items: center; }
    
    .legend-compact { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 8px; }
    .legend-item { display: flex; align-items: center; gap: 4px; font-size: 10px; color: var(--text-sec); }
    .legend-item .dot { width: 6px; height: 6px; border-radius: 50%; }

    /* Accordion / Listas M3 */
    .panel-card { border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.02);}
    .panel-header { display: flex; justify-content: space-between; align-items: center; padding: 14px; cursor: pointer; background: var(--surface); transition: 0.2s;}
    .panel-header:hover { background: #fdfdfd; }
    .title-row { display: flex; align-items: center; gap: 6px; }
    .panel-titles h3 { font-size: 12.5px; font-weight: 500; }
    .title-badge { background: #FCE8E6; color: var(--google-red); padding: 2px 6px; border-radius: 8px; font-size: 9.5px; font-weight: 600;}
    
    .list-group { display: flex; flex-direction: column; }
    .list-item { display: flex; align-items: center; padding: 10px 14px; gap: 10px; border-top: 1px solid var(--border); cursor: pointer; transition: 0.1s; }
    .list-item:hover { background: var(--surface-hover); }
    .item-icon { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; background: #F1F3F4; color: var(--text-sec); }
    .item-icon.alert { background: #FCE8E6; color: var(--google-red); }
    .item-content { flex: 1; display: flex; flex-direction: column; }
    .item-title { font-size: 11.5px; font-weight: 500; }
    .item-sub { font-size: 10px; color: var(--text-sec); margin-top: 1px;}
    .item-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
    
    .tag { font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 4px; }
    .tag.warning { background: #FEF7E0; color: #B06000; }
    .tag.success { background: #E6F4EA; color: #137333; }
    .tag.info { background: #E8F0FE; color: #174EA6; }
    .tag.neutral { background: #F1F3F4; color: var(--text-sec); }
    .date { font-size: 10px; color: var(--google-red); font-weight: 500;}

    /* Utilitários */
    .icon-btn { width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; color: var(--text-sec); transition: 0.2s; border: none; cursor: pointer; background: transparent;}
    .icon-btn:hover { background: var(--surface-hover); color: var(--text-main);}
    .icon-btn-close { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; color: var(--text-sec); transition: 0.2s; border: none; cursor: pointer; background: #F1F3F4;}
    .icon-btn-close:hover { background: #E8EAED; color: var(--text-main);}
    
    .custom-chart-tooltip { background: var(--surface); border: 1px solid var(--border); padding: 4px 8px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .tooltip-label { font-size: 10px; color: var(--text-sec); margin-bottom: 2px;}
    .tooltip-value { font-size: 12px; font-weight: 600; }

    /* Pesquisa por voz - overlay estilo Google */
    .voice-search-overlay { position: fixed; inset: 0; z-index: 9998; background: rgba(255, 255, 255, 0.86); backdrop-filter: blur(8px); display: grid; place-items: center; animation: fadeIn 0.18s ease; padding: 18px; }
    .voice-search-card { position: relative; width: min(360px, 92vw); min-height: 280px; background: var(--surface); border: 1px solid rgba(218, 220, 224, 0.86); border-radius: 28px; box-shadow: 0 24px 60px rgba(60, 64, 67, 0.18); display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; overflow: hidden; animation: popIn 0.24s cubic-bezier(0.16, 1, 0.3, 1); }
    .voice-big-mic { width: 86px; height: 86px; border-radius: 50%; background: var(--google-blue); color: #fff; display: grid; place-items: center; box-shadow: 0 14px 32px rgba(26, 115, 232, 0.32); z-index: 2; animation: voiceMicFloat 1.7s ease-in-out infinite; }
    .voice-big-mic svg { width: 42px; height: 42px; stroke-width: 1.7; }
    .voice-pulse-ring { position: absolute; top: 58px; width: 112px; height: 112px; border-radius: 50%; border: 2px solid rgba(26, 115, 232, 0.18); animation: voicePulse 1.8s ease-out infinite; }
    .voice-pulse-ring.ring-two { animation-delay: 0.55s; }
    .voice-search-card h3 { margin-top: 24px; font-size: 20px; font-weight: 600; color: var(--text-main); }
    .voice-search-card p { margin-top: 6px; color: var(--text-sec); font-size: 12px; line-height: 1.45; max-width: 250px; }
    .voice-wave { display: flex; align-items: center; justify-content: center; gap: 5px; height: 34px; margin-top: 18px; }
    .voice-wave span { width: 5px; height: 12px; border-radius: 999px; background: var(--google-blue); opacity: 0.76; animation: voiceWave 0.9s ease-in-out infinite; }
    .voice-wave span:nth-child(2) { animation-delay: 0.1s; }
    .voice-wave span:nth-child(3) { animation-delay: 0.2s; }
    .voice-wave span:nth-child(4) { animation-delay: 0.3s; }
    .voice-wave span:nth-child(5) { animation-delay: 0.4s; }

    /* Modais e Loaders */
    .global-loader-overlay { position: fixed; inset: 0; background: var(--surface); z-index: 9999; display: grid; place-items: center; }
    .loader-spinner { width: 32px; height: 32px; border: 3px solid #f3f3f3; border-top: 3px solid var(--google-blue); border-radius: 50%; animation: spin 1s linear infinite; }
    
    .loader-spinner-blue { width: 28px; height: 28px; border: 2px solid #E8F0FE; border-top: 2px solid var(--google-blue); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 10px;}

    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes pulseMic { 0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(217, 48, 37, 0.4); } 70% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(217, 48, 37, 0); } 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(217, 48, 37, 0); } }
    @keyframes voicePulse { 0% { transform: scale(0.68); opacity: 0.75; } 100% { transform: scale(1.55); opacity: 0; } }
    @keyframes voiceWave { 0%, 100% { height: 10px; opacity: 0.45; } 50% { height: 30px; opacity: 1; } }
    @keyframes voiceMicFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }

    .modal-backdrop { position: fixed; inset: 0; background: rgba(32, 33, 36, 0.6); backdrop-filter: blur(2px); z-index: 9999; display: flex; justify-content: center; align-items: center; padding: 16px; }
    .modal-box.qr-modal { background: var(--surface); border-radius: 16px; width: 100%; max-width: 320px; padding: 20px; box-shadow: 0 24px 48px rgba(0,0,0,0.2); animation: popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);}
    .modal-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
    .modal-titles h3 { font-size: 16px; font-weight: 500; color: var(--text-main); }
    .modal-titles p { font-size: 11px; color: var(--text-sec); margin-top: 4px; line-height: 1.4;}
    
    .qr-container { background: #F8FAFD; border-radius: 12px; padding: 20px; display: flex; justify-content: center; align-items: center; border: 1px solid var(--border); min-height: 220px;}
    .qr-container img { max-width: 100%; border-radius: 6px; }
    .qr-code-fade-in { animation: fadeIn 0.5s ease; }
    .qr-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-sec); font-size: 11px; font-weight: 500;}

    @keyframes popIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

    .system-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 10px 16px; border-radius: 30px; background: #323232; color: #fff; font-size: 11.5px; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 9999; }

    /* MOBILE ADJUSTMENTS */
    .mobile-only { display: none; }
    @media (max-width: 768px) {
      .mobile-only { display: block; }
      .desktop-only { display: none !important; }
      
      .app-sidebar { position: fixed; left: -240px; width: 240px; height: 100dvh; box-shadow: 2px 0 8px rgba(0,0,0,0.1); border-radius: 0 12px 12px 0; }
      .app-sidebar.open { left: 0; }
      
      .app-topbar { padding: 0 6px; gap: 6px; height: 50px; }
      .topbar-left { width: auto; }
      .topbar-center { margin: 0; flex: 1; }
      .topbar-right { width: auto; }
      
      .search-omnibox { height: 34px; padding: 0 6px 0 10px; }
      .search-omnibox input { font-size: 12px; }
      
      .dashboard-header-row { flex-direction: column; align-items: flex-start; gap: 12px; }
      .btn-nova-venda { width: 100%; justify-content: center; height: 40px; }
      
      .kpi-grid, .charts-grid { grid-template-columns: 1fr; }
      .content-scroll { padding: 12px; }
    }


    /* PREMIUM REDESIGN - Lumix Óticas */
    :root { --premium-bg:#f6f8fc; --premium-border:rgba(218,220,224,.82); --premium-shadow:0 14px 40px rgba(60,64,67,.10); --premium-soft-shadow:0 8px 24px rgba(60,64,67,.08); }
    .vizum-app-shell { background: radial-gradient(circle at top left, rgba(66,133,244,.12), transparent 32%), var(--premium-bg); }
    .app-sidebar { background: transparent; padding: 8px 0 8px 8px; }
    .app-main { margin: 8px 8px 8px 0; border-radius: 28px; border: 1px solid rgba(218,220,224,.78); box-shadow: 0 18px 50px rgba(60,64,67,.08); background: rgba(255,255,255,.86); backdrop-filter: blur(18px); }
    .app-topbar { height: 60px; padding: 0 18px; background: rgba(255,255,255,.72); backdrop-filter: blur(18px); }
    .brand-header { height: 60px; } .brand-logo { color:#202124; font-size:15px; font-weight:800; }
    .brand-logo-mark { width:38px; height:38px; border-radius:16px; background:#fff; box-shadow:0 10px 24px rgba(60,64,67,.10); }
    .nav-menu { padding: 8px 8px 12px 0; gap: 9px; }
    .nav-section { border-radius: 24px; padding: 7px; border: 1px solid transparent; transition:.22s ease; }
    .nav-section.open { background: rgba(255,255,255,.62); border-color: rgba(218,220,224,.72); box-shadow: 0 8px 22px rgba(60,64,67,.06); }
    .nav-section-special { background: linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg,#4285F4,#EA4335,#FBBC04,#34A853) border-box; border:1px solid transparent; box-shadow:0 10px 28px rgba(26,115,232,.12); }
    .nav-section-header { min-height:40px; padding:6px 8px; border-radius:18px; }
    .nav-section-title { font-size:11px; font-weight:900; color:#202124; } .nav-section-helper { font-size:9.5px; color:#5f6368; margin-top:2px; display:block; }
    .nav-item { height:38px; border-radius:18px; font-weight:700; color:#3c4043; } .nav-item.active { background:#e8f0fe; color:#174ea6; box-shadow: inset 3px 0 0 #1a73e8; }
    .search-omnibox { height:42px; border-radius:999px; background:#fff; box-shadow:0 6px 18px rgba(60,64,67,.06); }
    .content-scroll { background: transparent; padding:18px; }
    .premium-dashboard { max-width:1320px; margin:0 auto; display:flex; flex-direction:column; gap:16px; }
    .premium-hero-card { position:relative; overflow:hidden; min-height:230px; border-radius:32px; background:linear-gradient(135deg,rgba(255,255,255,.96),rgba(248,250,253,.9)); border:1px solid var(--premium-border); box-shadow:var(--premium-shadow); padding:28px; display:grid; grid-template-columns:minmax(0,1.5fr) 310px; gap:20px; }
    .hero-glow { position:absolute; width:250px; height:250px; border-radius:999px; filter:blur(34px); opacity:.18; pointer-events:none; } .hero-blue{background:#4285f4; right:180px; top:-90px;} .hero-green{background:#34a853; right:-80px; bottom:-120px;}
    .premium-hero-main,.premium-hero-side{position:relative;z-index:1}.hero-eyebrow{display:inline-flex;align-items:center;gap:6px;padding:7px 10px;background:#fff;border:1px solid rgba(218,220,224,.8);border-radius:999px;color:#5f6368;font-size:11px;font-weight:800}.google-dot{width:7px;height:7px;border-radius:999px;display:inline-block}.google-dot.blue{background:#4285f4}.google-dot.red{background:#ea4335}.google-dot.yellow{background:#fbbc04}.google-dot.green{background:#34a853}
    .premium-hero-main h1{margin-top:18px;font-size:clamp(28px,4vw,44px);letter-spacing:-.055em;line-height:.96;color:#202124}.premium-hero-main p{margin-top:12px;max-width:650px;color:#5f6368;font-size:14px;line-height:1.55}.hero-action-row{display:flex;flex-wrap:wrap;gap:10px;margin-top:24px}.hero-primary-action,.hero-secondary-action,.tiny-action{display:inline-flex;align-items:center;justify-content:center;gap:8px;border-radius:999px;transition:.18s ease;font-weight:900}.hero-primary-action{height:44px;padding:0 18px;background:#1a73e8;color:#fff;box-shadow:0 10px 24px rgba(26,115,232,.24)}.hero-secondary-action{height:44px;padding:0 16px;background:#fff;color:#202124;border:1px solid rgba(218,220,224,.86)}
    .premium-hero-side{display:flex;flex-direction:column;gap:12px;justify-content:flex-end}.store-filter-card,.hero-wa-status{background:rgba(255,255,255,.82);border:1px solid rgba(218,220,224,.8);border-radius:24px;padding:14px;box-shadow:var(--premium-soft-shadow)}.mini-label{color:#5f6368;font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:.08em}.hero-store-filter{margin-top:8px;width:100%;height:42px;border-radius:16px}.hero-wa-status{display:flex;align-items:center;gap:12px}.wa-orb{width:42px;height:42px;display:grid;place-items:center;border-radius:16px;color:#5f6368;background:#f1f3f4}.hero-wa-status.connected .wa-orb{background:#e6f4ea;color:#137333}.hero-wa-status strong{display:block;font-size:13px}.hero-wa-status span{display:block;color:#5f6368;margin-top:2px}
    .premium-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}.premium-kpi-card{position:relative;overflow:hidden;border-radius:26px;padding:18px;min-height:132px;background:#fff;border:1px solid rgba(218,220,224,.82);box-shadow:var(--premium-soft-shadow)}.premium-kpi-card::after{content:'';position:absolute;inset:auto -30px -60px auto;width:130px;height:130px;border-radius:50%;opacity:.12}.revenue-today::after{background:#4285f4}.revenue-month::after{background:#34a853}.ticket-card::after{background:#fbbc04}.lab-card::after{background:#ea4335}.kpi-topline{display:flex;justify-content:space-between;align-items:center;color:#5f6368;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.06em}.premium-kpi-card strong{display:block;margin-top:18px;font-size:25px;color:#202124;letter-spacing:-.04em}.premium-kpi-card p{margin-top:6px;color:#5f6368;font-weight:600}
    .smart-workspace-grid{display:grid;grid-template-columns:minmax(0,1.6fr) 360px;gap:14px}.smart-lower-grid{display:grid;grid-template-columns:minmax(0,1fr) 420px;gap:14px}.workspace-main-card,.mini-panel,.priority-card-premium{background:rgba(255,255,255,.92);border:1px solid rgba(218,220,224,.82);border-radius:28px;box-shadow:var(--premium-soft-shadow);padding:18px}.workspace-card-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}.workspace-card-header h3{margin-top:3px;font-size:18px;letter-spacing:-.035em;color:#202124}.tiny-action{height:34px;padding:0 12px;background:#f8fafd;border:1px solid rgba(218,220,224,.82);color:#1a73e8;font-size:11px}.premium-chart-wrapper{border-radius:22px;background:linear-gradient(180deg,#fff,#f8fafd);padding:6px}.workspace-side-column{display:flex;flex-direction:column;gap:14px}.crm-panel{min-height:150px;cursor:pointer;display:flex;align-items:center;gap:14px;background:linear-gradient(#fff,#fff) padding-box,linear-gradient(135deg,#4285F4,#EA4335,#FBBC04,#34A853) border-box;border:1px solid transparent;transition:.2s ease}.crm-panel:hover{transform:translateY(-2px);box-shadow:0 18px 36px rgba(26,115,232,.16)}.crm-panel-icon{width:52px;height:52px;border-radius:20px;display:grid;place-items:center;color:#1a73e8;background:#e8f0fe;flex-shrink:0}.crm-panel h3{font-size:18px;letter-spacing:-.035em;margin:4px 0}.crm-panel p{color:#5f6368;line-height:1.35}.priority-count{min-width:28px;height:28px;border-radius:999px;display:grid;place-items:center;background:#fce8e6;color:#c5221f;font-weight:900}.premium-priority-list{max-height:226px;overflow:auto;display:flex;flex-direction:column;gap:8px}.premium-priority-item{width:100%;display:grid;grid-template-columns:36px 1fr auto;align-items:center;gap:10px;padding:10px;border-radius:18px;background:#f8fafd;border:1px solid rgba(218,220,224,.72);text-align:left;transition:.18s ease}.priority-icon{width:36px;height:36px;border-radius:14px;display:grid;place-items:center;background:#fce8e6;color:#d93025}.priority-text strong{display:block;font-size:12px;color:#202124}.priority-text small{display:block;color:#5f6368;margin-top:2px}.priority-tag{padding:5px 8px;border-radius:999px;font-size:10px;font-weight:900;background:#e8f0fe;color:#174ea6}.priority-tag.warning{background:#fef7e0;color:#b06000}.priority-tag.success{background:#e6f4ea;color:#137333}.premium-empty-state{height:210px;display:grid;place-items:center;align-content:center;text-align:center;gap:7px;color:#5f6368}.empty-orb{width:52px;height:52px;border-radius:20px;display:grid;place-items:center;background:#e6f4ea;color:#137333}.module-container{background:rgba(255,255,255,.78);border:1px solid rgba(218,220,224,.72);border-radius:28px;box-shadow:var(--premium-soft-shadow);padding:16px;min-height:calc(100dvh - 96px)}
    @media(max-width:1120px){.premium-hero-card,.smart-workspace-grid,.smart-lower-grid{grid-template-columns:1fr}.premium-hero-side{flex-direction:row}.premium-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
    @media(max-width:768px){.app-main{margin:0;border-radius:0}.premium-hero-card{border-radius:24px;padding:18px}.premium-hero-side{flex-direction:column}.hero-action-row{display:grid;grid-template-columns:1fr}.premium-kpi-grid{grid-template-columns:1fr}.workspace-main-card,.mini-panel,.priority-card-premium{border-radius:22px;padding:14px}.premium-hero-main h1{font-size:30px}}

    /* CLEAN REDESIGN - arroz com feijão premium */
    :root { --clean-bg:#F8FAFD; --clean-card:#FFFFFF; --clean-border:#E0E3E7; --clean-text:#202124; --clean-muted:#5F6368; --clean-blue:#1A73E8; --clean-blue-soft:#E8F0FE; --clean-shadow:0 1px 2px rgba(60,64,67,.08),0 1px 3px rgba(60,64,67,.06); }
    .vizum-app-shell{background:var(--clean-bg)!important}.app-sidebar{background:var(--clean-bg)!important;padding:8px!important}.app-main{margin:8px 8px 8px 0!important;border-radius:18px!important;border:1px solid var(--clean-border)!important;box-shadow:none!important;background:#fff!important;backdrop-filter:none!important}.app-topbar{height:56px!important;padding:0 16px!important;background:#fff!important;backdrop-filter:none!important;border-bottom:1px solid #F1F3F4!important}.brand-header{height:56px!important;padding:0 8px!important}.brand-logo{color:var(--clean-text)!important;font-size:14px!important;font-weight:700!important}.brand-logo-mark{width:32px!important;height:32px!important;border-radius:10px!important;background:#fff!important;border:1px solid var(--clean-border)!important;box-shadow:none!important;color:var(--clean-text)!important}
    .nav-menu{padding:4px 0 8px!important;gap:6px!important}.nav-section{border-radius:12px!important;padding:4px!important;border:1px solid transparent!important;background:transparent!important;box-shadow:none!important}.nav-section.open{background:#fff!important;border-color:var(--clean-border)!important;box-shadow:var(--clean-shadow)!important}.nav-section-special{background:#fff!important;border:1px solid var(--clean-border)!important;box-shadow:var(--clean-shadow)!important}.nav-section-special::before{display:none!important}.nav-section-header{min-height:34px!important;padding:6px 8px!important;border-radius:8px!important;color:var(--clean-muted)!important}.nav-section-special .nav-section-header{padding-left:8px!important;color:var(--clean-muted)!important}.nav-section-title{font-size:10px!important;font-weight:800!important;text-transform:uppercase;letter-spacing:.05em;color:var(--clean-muted)!important}.nav-section-helper{display:none!important}.nav-section-items{gap:2px!important;margin-top:2px!important}.nav-item{height:36px!important;border-radius:8px!important;padding:0 10px!important;font-weight:600!important;color:#3C4043!important;background:transparent!important;box-shadow:none!important}.nav-item:hover{background:#F1F3F4!important}.nav-item.active{background:var(--clean-blue-soft)!important;color:#174EA6!important;box-shadow:inset 3px 0 0 var(--clean-blue)!important}.nav-item.featured{font-weight:700!important}
    .search-omnibox{height:40px!important;border-radius:12px!important;background:#F8FAFD!important;border:1px solid var(--clean-border)!important;box-shadow:none!important}.search-omnibox.focused{background:#fff!important;border-color:#C9D7F8!important;box-shadow:0 0 0 3px rgba(26,115,232,.10)!important;border-radius:12px 12px 0 0!important}.content-scroll{background:#fff!important;padding:16px!important}.clean-dashboard{max-width:1180px!important;margin:0 auto!important;display:flex!important;flex-direction:column!important;gap:12px!important}.clean-header-card,.clean-metric-card,.clean-card{background:var(--clean-card);border:1px solid var(--clean-border);border-radius:14px;box-shadow:var(--clean-shadow)}.clean-header-card{min-height:128px;padding:18px;display:grid;grid-template-columns:minmax(0,1fr) 360px;gap:16px;align-items:center}.clean-eyebrow{display:inline-flex;color:var(--clean-blue);font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}.clean-header-info h1{font-size:28px;line-height:1.05;letter-spacing:-.04em;color:var(--clean-text);margin:0}.clean-header-info p{margin-top:8px;color:var(--clean-muted);font-size:13px;line-height:1.45}.clean-header-actions{display:flex;flex-direction:column;gap:10px}.clean-store-select>span{display:block;color:var(--clean-muted);font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;margin-bottom:5px}.clean-store-chip{width:100%;height:38px;border-radius:10px;background:#F8FAFD;border:1px solid var(--clean-border);box-shadow:none}.clean-action-row{display:grid;grid-template-columns:1fr 1fr;gap:8px}.clean-primary-btn,.clean-secondary-btn,.clean-link-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border-radius:10px;font-weight:700;transition:.15s ease}.clean-primary-btn{height:38px;background:var(--clean-blue);color:#fff}.clean-primary-btn:hover{background:#1765CC}.clean-secondary-btn{height:38px;background:#fff;color:var(--clean-text);border:1px solid var(--clean-border)}.clean-secondary-btn:hover,.clean-link-btn:hover{background:#F8FAFD}
    .clean-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}.clean-metric-card{min-height:104px;padding:14px}.clean-metric-card span{display:block;color:var(--clean-muted);font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.05em}.clean-metric-card strong{display:block;margin-top:14px;font-size:22px;line-height:1;letter-spacing:-.035em;color:var(--clean-text)}.clean-metric-card p{margin-top:8px;color:var(--clean-muted);font-size:12px;font-weight:600}.clean-main-grid{display:grid;grid-template-columns:minmax(0,1.65fr) 320px;gap:12px}.clean-bottom-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(360px,.85fr);gap:12px}.clean-card{padding:14px;min-width:0}.clean-card-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px}.clean-card-header span{display:block;color:var(--clean-muted);font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em}.clean-card-header h3{margin-top:3px;color:var(--clean-text);font-size:16px;letter-spacing:-.025em}.clean-link-btn{height:30px;padding:0 10px;color:var(--clean-blue);border:1px solid var(--clean-border);background:#fff;font-size:11px}.clean-chart-wrapper,.clean-seller-chart{background:#fff;border-radius:10px}.clean-status-body{display:flex;flex-direction:column;gap:8px}.clean-status-list{display:flex;flex-direction:column;gap:6px}.clean-status-item{display:grid;grid-template-columns:10px 1fr auto;align-items:center;gap:8px;min-height:30px;padding:0 8px;border-radius:8px;background:#F8FAFD;color:var(--clean-muted);font-weight:600}.clean-status-item strong{color:var(--clean-text)}.clean-count-badge{min-width:26px;height:26px;padding:0 8px;border-radius:999px;display:grid;place-items:center;background:#FCE8E6;color:#C5221F;font-size:12px}.clean-priority-list{max-height:210px;overflow:auto;display:flex;flex-direction:column;gap:6px}.clean-priority-item{width:100%;min-height:52px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:center;padding:10px;border-radius:10px;border:1px solid var(--clean-border);background:#fff;text-align:left;transition:.15s ease}.clean-priority-item:hover{background:#F8FAFD;border-color:#D2E3FC}.clean-priority-main strong{display:block;color:var(--clean-text);font-size:12px}.clean-priority-main small{display:block;color:var(--clean-muted);margin-top:3px;font-size:10px}.clean-tag{padding:5px 8px;border-radius:999px;background:#E8F0FE;color:#174EA6;font-size:10px;font-weight:800;white-space:nowrap}.clean-tag.warning{background:#FEF7E0;color:#B06000}.clean-tag.success{background:#E6F4EA;color:#137333}.clean-tag.info{background:#E8F0FE;color:#174EA6}.clean-empty-state{min-height:130px;display:grid;place-items:center;align-content:center;text-align:center;gap:5px;color:var(--clean-muted);background:#F8FAFD;border:1px dashed var(--clean-border);border-radius:12px}.clean-empty-state strong{color:var(--clean-text)}.module-container{background:#fff!important;border:1px solid var(--clean-border)!important;border-radius:14px!important;box-shadow:var(--clean-shadow)!important;padding:14px!important;min-height:calc(100dvh - 88px)!important}
    @media(max-width:1100px){.clean-header-card,.clean-main-grid,.clean-bottom-grid{grid-template-columns:1fr}.clean-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:768px){.app-main{margin:0!important;border-radius:0!important;border:none!important}.content-scroll{padding:12px!important}.clean-header-card{padding:14px}.clean-header-info h1{font-size:24px}.clean-kpi-grid{grid-template-columns:1fr}.clean-action-row{grid-template-columns:1fr}}

  `}</style>
);